import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Receipt,
  Truck,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Clock,
  Euro,
  FileText,
  Calendar,
  CreditCard,
  Banknote,
  Search,
  Filter,
  Lock,
  BadgeCheck,
  Camera,
  ZoomIn,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  User,
  Users,
  Building2,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ConsumptionInvoicing() {
  const { hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const canFinalize = hasPermission ? hasPermission('invoicing.finalize') : false;
  const canConfigureSage = hasPermission ? hasPermission('sage.configure') : false;
  const [data, setData] = useState({ records: [], total_records: 0, total_paid: 0, total_unpaid: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [filterFinal, setFilterFinal] = useState('all');
  const [filterChanges, setFilterChanges] = useState('all'); // New filter for planned vs actual
  const [searchAp, setSearchAp] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // FSM AP data cache (for client names, tech info, changes indicator)
  const [fsmApData, setFsmApData] = useState({});
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Mark as invoiced modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [processing, setProcessing] = useState(false);

  // SAGE sync state
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncConfig, setSyncConfig] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configDocTypesInput, setConfigDocTypesInput] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  // Diagnostic
  const [diagTipo, setDiagTipo] = useState('FRM');
  const [diagSerie, setDiagSerie] = useState('');
  const [diagCount, setDiagCount] = useState(10);
  const [diagNumero, setDiagNumero] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const runDiagnostic = async () => {
    if (!diagTipo.trim()) { toast.error('Tipo obrigatório'); return; }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const params = new URLSearchParams({
        tipo: diagTipo.trim().toUpperCase(),
        count: String(diagCount || 10),
      });
      if (diagSerie.trim()) params.append('serie', diagSerie.trim());
      if (diagNumero.trim()) params.append('numero', diagNumero.trim());
      const res = await axios.post(`${API}/consumptions/sync-sage-invoices/diagnose?${params.toString()}`);
      setDiagResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro';
      toast.error(msg);
    } finally {
      setDiagLoading(false);
    }
  };

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/consumptions/sync-sage-invoices/status`);
      setSyncStatus(res.data);
    } catch (e) {
      // status is non-critical
    }
  }, []);

  const fetchSyncConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/consumptions/sync-sage-invoices/config`);
      setSyncConfig(res.data);
      setConfigDocTypesInput((res.data?.doc_types || []).join(', '));
    } catch (e) {
      // permission-based; ignore if forbidden
    }
  }, []);

  const saveSyncConfig = async () => {
    const parsed = configDocTypesInput
      .split(/[,\s]+/)
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);
    if (!parsed.length) {
      toast.error('Indica pelo menos um tipo de documento');
      return;
    }
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API}/consumptions/sync-sage-invoices/config`, {
        doc_types: parsed,
      });
      toast.success(`Configuração atualizada (${res.data.doc_types.length} tipos). Cursor reiniciado.`);
      setSyncConfig((prev) => ({ ...(prev || {}), doc_types: res.data.doc_types, is_custom: true }));
      setShowConfigDialog(false);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro a guardar configuração';
      toast.error(msg);
    } finally {
      setSavingConfig(false);
    }
  };

  const runSageSync = async () => {
    setSyncing(true);
    try {
      const prevRanAt = syncStatus?.ran_at || null;
      const res = await axios.post(`${API}/consumptions/sync-sage-invoices`);
      const data = res.data || {};
      if (!data.success) {
        toast.error(data.message || 'Falha na sincronização SAGE');
        return;
      }
      toast.info(data.already_running
        ? 'Sincronização já está a decorrer — a aguardar resultado...'
        : 'Sincronização SAGE iniciada em segundo plano...');
      // Poll status until this run finishes (max ~8 min)
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const st = (await axios.get(`${API}/consumptions/sync-sage-invoices/status`)).data;
          setSyncStatus(st);
          if (!st.running && st.ran_at && st.ran_at !== prevRanAt) {
            const closed = st.matches || 0;
            const linked = (st.linked || []).length;
            if (closed > 0 || linked > 0) {
              const parts = [];
              if (closed > 0) parts.push(`${closed} AP(s) fechado(s)`);
              if (linked > 0) parts.push(`${linked} fatura(s) associada(s)`);
              toast.success(`✅ ${parts.join(' · ')} via SAGE`);
            } else {
              toast.info(`SAGE varrido (${st.scanned || 0} docs). Sem novos APs para fechar ou faturar.`);
            }
            // Always refresh the list — the scheduler may also have linked
            // invoices since the page loaded.
            fetchData();
            return;
          }
        } catch (e) {
          // keep polling
        }
      }
      toast.info('A sincronização continua em segundo plano. Verifica o estado mais tarde.');
      fetchData();
    } catch (error) {
      console.error('SAGE sync error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com SAGE');
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') {
        params.payment_status = filterStatus;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }
      
      const response = await axios.get(`${API}/consumptions/invoicing`, { params });
      setData(response.data);
      
      // Fetch FSM AP data for records that have fsm_service_appointment_id
      const fsmIds = response.data.records
        .filter(r => r.fsm_service_appointment_id)
        .map(r => r.fsm_service_appointment_id);
      
      if (fsmIds.length > 0) {
        // Fetch FSM AP data in batch (or individually for now)
        const fsmDataMap = {};
        for (const apId of fsmIds.slice(0, 50)) { // Limit to first 50 for performance
          try {
            const apResponse = await axios.get(`${API}/fsm/appointments/${apId}`);
            fsmDataMap[apId] = apResponse.data;
          } catch (e) {
            // AP may not exist or user may not have access
          }
        }
        setFsmApData(fsmDataMap);
      }
    } catch (error) {
      console.error('Error fetching invoicing data:', error);
      toast.error('Erro ao carregar dados de faturação');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
    fetchSyncStatus();
    fetchSyncConfig();
  }, [fetchData, fetchSyncStatus, fetchSyncConfig]);

  // Filter records by AP search + fecho final state
  const isInvoiced = (r) => !!(r.sage_invoice_ref || r.invoiced_at || r.invoice_number);
  const canFinalClose = (r) => r.status === 'closed' && r.payment_status === 'paid' && isInvoiced(r) && !r.final_closed;
  
  // Helper: Detect planned vs actual changes
  const getChangesInfo = (record) => {
    const apId = record.fsm_service_appointment_id;
    const ap = fsmApData[apId];
    
    if (!ap) return { hasChanges: false, changes: [] };
    
    const changes = [];
    const plannedParts = ap.planned_parts || [];
    const actualParts = ap.actual_parts || [];
    const plannedServices = ap.planned_services || [];
    const actualServices = ap.actual_services || [];
    const notUsed = ap.confirmed_not_used || [];
    
    // Check for not used parts
    if (notUsed.length > 0) {
      changes.push({ type: 'not_used', count: notUsed.length, label: `${notUsed.length} material não utilizado` });
    }
    
    // Check for added parts (technician_added)
    const addedParts = actualParts.filter(p => p.source === 'technician_added');
    if (addedParts.length > 0) {
      changes.push({ type: 'added_part', count: addedParts.length, label: `${addedParts.length} material adicionado` });
    }
    
    // Check for substitutions
    const substitutions = actualParts.filter(p => p.source === 'substitution');
    if (substitutions.length > 0) {
      changes.push({ type: 'substitution', count: substitutions.length, label: `${substitutions.length} substituição` });
    }
    
    // Check for quantity changes in planned parts
    const qtyChanges = actualParts.filter(p => {
      if (p.source !== 'planned') return false;
      const planned = plannedParts.find(pp => pp.sku === p.sku || pp.fsm_part_id === p.fsm_part_id);
      return planned && planned.quantity !== p.quantity;
    });
    if (qtyChanges.length > 0) {
      changes.push({ type: 'qty_change', count: qtyChanges.length, label: `${qtyChanges.length} quantidade alterada` });
    }
    
    // Check for added services
    const addedServices = actualServices.filter(s => s.source === 'technician_added');
    if (addedServices.length > 0) {
      changes.push({ type: 'added_service', count: addedServices.length, label: `${addedServices.length} serviço adicionado` });
    }
    
    return { hasChanges: changes.length > 0, changes };
  };
  
  // Helper: Get client display info from FSM AP
  const getClientInfo = (record) => {
    const apId = record.fsm_service_appointment_id;
    const ap = fsmApData[apId];
    
    if (ap) {
      return {
        name: ap.client_contact_name || ap.customer_name || null,
        nif: ap.client_nif || ap.customer_nif || null,
        company: ap.client_company_name || null
      };
    }
    
    // Fallback to consumption record data
    return {
      name: record.client_name || null,
      nif: record.client_nif || null,
      company: null
    };
  };
  
  // Helper: Get tech/lead display info from FSM AP
  const getTechInfo = (record) => {
    const apId = record.fsm_service_appointment_id;
    const ap = fsmApData[apId];
    
    if (ap) {
      const resources = ap.assigned_resources || [];
      const lead = resources.find(r => r.is_lead);
      const otherCount = resources.length - (lead ? 1 : 0);
      
      return {
        leadName: lead?.display_name || ap.fsm_service_resource_name || ap.stockflow_user_name || record.technician_name,
        otherCount: otherCount > 0 ? otherCount : 0
      };
    }
    
    return {
      leadName: record.technician_name || 'N/A',
      otherCount: 0
    };
  };
  
  const filteredRecords = data.records.filter(record => {
    if (searchAp) {
      const q = searchAp.toLowerCase();
      // Get FSM AP data for enhanced search
      const ap = fsmApData[record.fsm_service_appointment_id];
      const clientInfo = getClientInfo(record);
      const techInfo = getTechInfo(record);
      
      const hay = [
        record.ap_number, 
        record.client_nif, record.client_name, record.client_email,
        record.sage_din_reference, record.sage_invoice_ref, record.invoice_number,
        record.warehouse_name, record.technician_name,
        // FSM data
        clientInfo.name, clientInfo.nif, clientInfo.company,
        techInfo.leadName,
        ap?.customer_name, ap?.client_contact_name, ap?.customer_nif
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterFinal === 'done' && !record.final_closed) return false;
    if (filterFinal === 'pending' && record.final_closed) return false;
    // Filter by changes
    if (filterChanges === 'with_changes') {
      const { hasChanges } = getChangesInfo(record);
      if (!hasChanges) return false;
    }
    if (filterChanges === 'no_changes') {
      const { hasChanges } = getChangesInfo(record);
      if (hasChanges) return false;
    }
    // Auto-arquivo: APs concluídos há mais de 30 dias saem da vista ativa
    if (!showHistory && record.final_closed) {
      const ts = record.final_closed_at ? new Date(record.final_closed_at).getTime() : 0;
      if (!ts || ts < Date.now() - 30 * 24 * 3600 * 1000) return false;
    }
    return true;
  });

  // Group records by warehouse
  const groupedByWarehouse = filteredRecords.reduce((acc, record) => {
    const wh = record.warehouse_code || 'N/A';
    if (!acc[wh]) {
      acc[wh] = {
        warehouse_code: wh,
        warehouse_name: record.warehouse_name || wh,
        records: [],
        total_value: 0,
        total_paid: 0,
        total_unpaid: 0,
        breakdown_items: 0,
        breakdown_labor: 0,
        breakdown_travel: 0,
        breakdown_other: 0,
      };
    }
    acc[wh].records.push(record);
    acc[wh].total_value += record.total_value || 0;
    // Breakdown by component (items vs extras). Fallback: if `items_value` not
    // present (legacy records), recompute from items array.
    const itemsValue = record.items_value !== undefined && record.items_value !== null
      ? Number(record.items_value)
      : (record.items || []).reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 0), 0);
    acc[wh].breakdown_items += itemsValue;
    acc[wh].breakdown_labor += Number(record.labor_value || 0);
    acc[wh].breakdown_travel += Number(record.travel_value || 0);
    acc[wh].breakdown_other += Number(record.other_value || 0);
    if (record.payment_status === 'paid') {
      acc[wh].total_paid += record.total_value || 0;
    } else {
      acc[wh].total_unpaid += record.total_value || 0;
    }
    return acc;
  }, {});

  const toggleWarehouse = (warehouseCode) => {
    setExpandedRecords(prev => ({
      ...prev,
      [warehouseCode]: !prev[warehouseCode]
    }));
  };

  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const openInvoiceModal = (record) => {
    setSelectedRecord(record);
    setInvoiceNumber('');
    setShowInvoiceModal(true);
  };

  const markAsInvoiced = async () => {
    if (!selectedRecord) return;
    
    setProcessing(true);
    try {
      const params = new URLSearchParams();
      if (invoiceNumber) {
        params.append('invoice_number', invoiceNumber);
      }
      params.append('invoiced_at', new Date().toISOString());
      
      await axios.patch(`${API}/consumptions/${selectedRecord.id}?${params.toString()}`);
      toast.success('Consumo marcado como faturado');
      setShowInvoiceModal(false);
      fetchData();
    } catch (error) {
      console.error('Error marking as invoiced:', error);
      toast.error('Erro ao marcar como faturado');
    } finally {
      setProcessing(false);
    }
  };

  const markAsPaid = async (record) => {
    try {
      await axios.patch(`${API}/consumptions/${record.id}?payment_status=paid`);
      toast.success(`AP ${record.ap_number} marcado como pago`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar como pago');
    }
  };

  const finalClose = async (record) => {
    try {
      const res = await axios.post(`${API}/consumptions/${record.id}/final-close`);
      toast.success(res.data?.message || `AP ${record.ap_number} concluído ✓`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no fecho final');
    }
  };

  const handleRetryDin = async (record) => {
    try {
      const res = await axios.post(`${API}/consumptions/${record.id}/create-din`);
      toast.success(`${res.data?.document_reference || 'DIN'} criado no SAGE`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar DIN no SAGE');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'mbway': return <CreditCard className="h-4 w-4" />;
      case 'cash': return <Banknote className="h-4 w-4" />;
      case 'card': return <CreditCard className="h-4 w-4" />;
      case 'transfer': return <Euro className="h-4 w-4" />;
      default: return <Receipt className="h-4 w-4" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'mbway': 'MBWay',
      'cash': 'Numerário',
      'card': 'MB',
      'transfer': 'Transferência'
    };
    return labels[method] || method || 'N/A';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Faturação de Serviços
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                O fecho final (Admin) exige 3 controlos: <strong>fechado pelo técnico</strong> · <strong>pago</strong> · <strong>faturado</strong>
              </p>
              {syncStatus?.ran_at && (
                <p className="text-[11px] text-slate-400 mt-1" data-testid="sync-last-run">
                  Última sincronização: {formatDate(syncStatus.ran_at)} · {syncStatus.scanned || 0} docs · {syncStatus.matches || 0} APs fechados
                </p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {canConfigureSage && (
                <>
                  <Button
                    variant="default"
                    onClick={runSageSync}
                    disabled={syncing || syncStatus?.running}
                    data-testid="sync-sage-btn"
                    className="bg-[#C41E3A] hover:bg-[#a01830]"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${(syncing || syncStatus?.running) ? 'animate-spin' : ''}`} />
                    {(syncing || syncStatus?.running) ? 'A sincronizar...' : 'Sincronizar SAGE'}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowConfigDialog(true)}
                    title="Configurar tipos de documento"
                    data-testid="sync-config-btn"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={fetchData}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sync-config-dialog">
          <DialogHeader>
            <DialogTitle>Configuração e Diagnóstico do Sync SAGE</DialogTitle>
            <DialogDescription>
              Ajusta a lista de tipos varridos e/ou diagnostica documentos específicos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Configuration section */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Tipos de documento a varrer</h4>
              <Input
                value={configDocTypesInput}
                onChange={(e) => setConfigDocTypesInput(e.target.value)}
                placeholder="FT, FR, FRM, FRN, FRT, FS, VD, NC..."
                className="font-mono"
                data-testid="sync-config-input"
              />
              {syncConfig?.defaults && (
                <p className="text-[11px] text-slate-500">
                  Predefinidos: <span className="font-mono">{syncConfig.defaults.join(', ')}</span>
                </p>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                ⚠️ Ao guardar, o cursor de progresso é reiniciado — todos os tipos são re-varridos.
              </div>
              <Button onClick={saveSyncConfig} disabled={savingConfig} size="sm" data-testid="save-sync-config-btn">
                {savingConfig ? 'A guardar...' : 'Guardar e re-varrer'}
              </Button>
            </div>

            <div className="h-px bg-slate-200" />

            {/* Diagnostic section */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Diagnóstico</h4>
              <p className="text-[11px] text-slate-500">
                Vê os últimos N documentos de um tipo/série no SAGE com o respectivo <code className="bg-slate-100 px-1 rounded">VossoNumero</code> — útil para descobrir porque é que um AP não está a ser fechado.
              </p>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[80px]">
                  <label className="text-[11px] text-slate-500">Tipo</label>
                  <Input value={diagTipo} onChange={(e) => setDiagTipo(e.target.value)} placeholder="FRM" className="font-mono h-9" data-testid="diag-tipo-input" />
                </div>
                <div className="flex-1 min-w-[80px]">
                  <label className="text-[11px] text-slate-500">Série (opcional)</label>
                  <Input value={diagSerie} onChange={(e) => setDiagSerie(e.target.value)} placeholder="3M" className="font-mono h-9" data-testid="diag-serie-input" />
                </div>
                <div className="w-24">
                  <label className="text-[11px] text-slate-500">Nº exato</label>
                  <Input value={diagNumero} onChange={(e) => setDiagNumero(e.target.value)} placeholder="4918" className="font-mono h-9" data-testid="diag-numero-input" />
                </div>
                <div className="w-16">
                  <label className="text-[11px] text-slate-500">N</label>
                  <Input type="number" min="1" max="50" value={diagCount} onChange={(e) => setDiagCount(parseInt(e.target.value, 10) || 10)} className="h-9" data-testid="diag-count-input" disabled={!!diagNumero.trim()} />
                </div>
                <Button onClick={runDiagnostic} disabled={diagLoading} size="sm" data-testid="run-diag-btn">
                  {diagLoading ? 'A consultar SAGE...' : (diagNumero.trim() ? 'Consultar doc' : 'Diagnosticar')}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 -mt-1">
                💡 Se sabes o número exacto do doc, indica-o em <strong>Nº exato</strong> para bypass do discovery.
              </p>

              {diagResult && diagResult.mode === 'direct_probe' && (
                <div className="mt-2 border rounded-lg overflow-hidden" data-testid="diag-direct-probe">
                  <div className="bg-slate-50 px-3 py-2 text-xs flex justify-between">
                    <span className="font-mono font-semibold">{diagResult.tipo}/{diagResult.serie}/#{diagResult.numero}</span>
                    {diagResult.found_at_year !== null && (
                      <span className="text-emerald-600 text-xs font-medium">✓ Encontrado no ano {diagResult.found_at_year}</span>
                    )}
                    {diagResult.found_at_year === null && (
                      <span className="text-red-600 text-xs font-medium">✗ Não encontrado</span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100 text-xs">
                    {diagResult.attempts.map((a, idx) => (
                      <div key={`${a.ano_tentado}-${idx}`} className={`px-3 py-2 ${a.success ? 'bg-emerald-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono w-16">Ano={a.ano_tentado}</span>
                          {a.success ? (
                            <span className="text-emerald-600 font-medium">✓ SUCCESS</span>
                          ) : (
                            <span className="text-red-500">✗ {a.message || 'fail'}</span>
                          )}
                        </div>
                        {a.success && (
                          <div className="pl-16 mt-1 space-y-0.5 text-slate-600">
                            <div><span className="text-slate-400">VossoNumero:</span> <span className="font-mono">{a.vosso_numero || '(vazio)'}</span></div>
                            <div><span className="text-slate-400">Terceiro:</span> {a.terceiro}</div>
                            <div><span className="text-slate-400">Data:</span> {a.data}</div>
                            <div><span className="text-slate-400">Estado:</span> {a.estado}</div>
                            <div><span className="text-slate-400">Linhas:</span> {a.num_linhas}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diagResult && diagResult.mode !== 'direct_probe' && (
                <div className="mt-2 space-y-3" data-testid="diag-results">
                  {diagResult.raw_series_metadata?.length > 0 && (
                    <details className="border rounded-lg text-xs">
                      <summary className="cursor-pointer bg-slate-50 px-3 py-2 font-medium">
                        Metadata SAGE das séries ({diagResult.raw_series_metadata.length})
                      </summary>
                      <pre className="px-3 py-2 bg-slate-900 text-slate-100 text-[10px] overflow-x-auto rounded-b">
                        {JSON.stringify(diagResult.raw_series_metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                  {(diagResult.series || []).map((s) => (
                    <div key={s.serie} className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 text-xs flex justify-between">
                        <span className="font-mono font-semibold">{diagResult.tipo}/{s.serie}</span>
                        <span className="text-slate-500">max #{s.max_known} · {s.probes_used} probes</span>
                      </div>
                      {s.note && (
                        <p className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t">{s.note}</p>
                      )}
                      <ul className="divide-y divide-slate-100 text-xs">
                        {(s.recent_docs || []).map((d) => (
                          <li key={d.numero} className="px-3 py-1.5 flex items-center gap-2">
                            <span className="font-mono text-slate-600 w-16">#{d.numero}</span>
                            {d.found ? (
                              <>
                                <span className={`flex-1 truncate font-mono ${d.vosso_numero ? 'text-slate-900' : 'text-slate-300'}`}>
                                  {d.vosso_numero || '(sem VossoNumero)'}
                                </span>
                                <span className="text-slate-400 hidden sm:inline">{d.terceiro?.slice(0, 30)}</span>
                                <span className="text-slate-400 tabular-nums">{d.data}</span>
                              </>
                            ) : (
                              <span className="text-slate-300 italic">não encontrado</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.total_records}</p>
                <p className="text-xs text-muted-foreground">Total Serviços</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">€{data.total_paid.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Total Pago</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">€{data.total_unpaid.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Por Pagar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Euro className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">€{data.total_value.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Valor Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar AP, NIF, nome, DIN, fatura..."
                  value={searchAp}
                  onChange={(e) => setSearchAp(e.target.value)}
                  className="pl-10"
                  data-testid="search-ap-input"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Estado pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="unpaid">Por Pagar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterFinal} onValueChange={setFilterFinal}>
              <SelectTrigger className="w-[170px]" data-testid="filter-final">
                <Lock className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Fecho final" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Fecho final: todos</SelectItem>
                <SelectItem value="pending">Por concluir</SelectItem>
                <SelectItem value="done">Concluídos</SelectItem>
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setShowHistory(v => !v)}
              className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
                showHistory
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
              title="Vista ativa esconde APs concluídos há mais de 30 dias"
              data-testid="toggle-history-btn"
            >
              {showHistory ? 'Histórico completo' : 'Vista ativa (30d)'}
            </button>
            <div className="flex gap-2 items-center">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[140px]"
                data-testid="date-from"
              />
              <span className="text-muted-foreground">a</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[140px]"
                data-testid="date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records grouped by warehouse */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">A carregar...</p>
          </CardContent>
        </Card>
      ) : filteredRecords.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold">Sem serviços</h3>
            <p className="text-muted-foreground">Não há serviços fechados para faturar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.values(groupedByWarehouse).map(warehouse => (
            <Card key={warehouse.warehouse_code} data-testid={`warehouse-card-${warehouse.warehouse_code}`}>
              <Collapsible
                open={expandedRecords[warehouse.warehouse_code] !== false}
                onOpenChange={() => toggleWarehouse(warehouse.warehouse_code)}
              >
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedRecords[warehouse.warehouse_code] !== false ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                        <Truck className="h-5 w-5 text-blue-600" />
                        <div>
                          <CardTitle className="text-lg">
                            {warehouse.warehouse_name}
                          </CardTitle>
                          <CardDescription>
                            {warehouse.records.length} serviços · €{warehouse.total_value.toFixed(2)}
                          </CardDescription>
                          {(warehouse.breakdown_labor > 0 || warehouse.breakdown_travel > 0 || warehouse.breakdown_other > 0) && (
                            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500 font-mono" data-testid={`breakdown-${warehouse.warehouse_code}`}>
                              <span>€{warehouse.breakdown_items.toFixed(2)} <span className="font-sans text-slate-400">items</span></span>
                              {warehouse.breakdown_labor > 0 && (
                                <span className="text-blue-700">€{warehouse.breakdown_labor.toFixed(2)} <span className="font-sans text-blue-500/70">m.obra</span></span>
                              )}
                              {warehouse.breakdown_travel > 0 && (
                                <span className="text-amber-700">€{warehouse.breakdown_travel.toFixed(2)} <span className="font-sans text-amber-600/70">desloc.</span></span>
                              )}
                              {warehouse.breakdown_other > 0 && (
                                <span className="text-purple-700">€{warehouse.breakdown_other.toFixed(2)} <span className="font-sans text-purple-500/70">outros</span></span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {warehouse.total_unpaid > 0 && (
                          <Badge variant="outline" className="text-orange-600 border-orange-200">
                            €{warehouse.total_unpaid.toFixed(2)} por pagar
                          </Badge>
                        )}
                        <Badge className="bg-green-100 text-green-700">
                          €{warehouse.total_paid.toFixed(2)} pago
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>AP / Cliente</TableHead>
                            <TableHead className="hidden lg:table-cell">Técnico</TableHead>
                            <TableHead className="hidden md:table-cell">Data</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                            <TableHead>Pagamento</TableHead>
                            <TableHead className="hidden sm:table-cell">Estado</TableHead>
                            <TableHead className="text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {warehouse.records.map(record => {
                            const clientInfo = getClientInfo(record);
                            const techInfo = getTechInfo(record);
                            const changesInfo = getChangesInfo(record);
                            const hasFsmAp = !!record.fsm_service_appointment_id;
                            // Protect against false positive: if AP has no actual_* data yet, don't show changes badge
                            const ap = fsmApData[record.fsm_service_appointment_id];
                            const hasExecution = ap && (
                              (ap.actual_parts && ap.actual_parts.length > 0) ||
                              (ap.actual_services && ap.actual_services.length > 0) ||
                              (ap.confirmed_not_used && ap.confirmed_not_used.length > 0) ||
                              ap.tech_confirmed_at
                            );
                            const showChangesIndicator = changesInfo.hasChanges && hasExecution;
                            
                            return (
                            <TableRow key={record.id} data-testid={`record-row-${record.id}`}>
                              {/* Col 1: AP / Cliente */}
                              <TableCell>
                                <div className="space-y-0.5">
                                  {/* AP Number - clickable link to full AP */}
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {hasFsmAp ? (
                                      <button
                                        onClick={() => navigate(`/fsm-appointments?id=${record.fsm_service_appointment_id}`, { state: { returnTo: '/invoicing' } })}
                                        className="font-mono font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                        title="Abrir AP completa"
                                        data-testid={`ap-link-${record.id}`}
                                      >
                                        {record.ap_number}
                                        <ExternalLink className="w-3 h-3" />
                                      </button>
                                    ) : (
                                      <span className="font-mono font-medium" data-testid={`ap-legacy-${record.id}`}>{record.ap_number}</span>
                                    )}
                                    {record.sage_din_reference && (
                                      <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 font-mono" title="DIN SAGE" data-testid={`din-badge-${record.id}`}>
                                        {record.sage_din_reference}
                                      </Badge>
                                    )}
                                    {showChangesIndicator && (
                                      <Badge 
                                        className="bg-amber-100 text-amber-700 text-[10px] px-1.5 cursor-help"
                                        title={changesInfo.changes.map(c => c.label).join('\n')}
                                        data-testid={`changes-badge-${record.id}`}
                                      >
                                        <AlertTriangle className="w-3 h-3 mr-0.5" />
                                        Alterações
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Client info */}
                                  {clientInfo.name ? (
                                    <div className="text-xs text-gray-600 flex items-center gap-1">
                                      <Building2 className="w-3 h-3 text-gray-400 shrink-0" />
                                      <span className="truncate max-w-[180px]" title={clientInfo.name}>{clientInfo.name}</span>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400">Cliente —</div>
                                  )}
                                  
                                  {/* NIF / Invoice request */}
                                  {record.wants_invoice ? (
                                    <div className="text-xs text-blue-600 flex items-center gap-1">
                                      <FileText className="w-3 h-3 shrink-0" />
                                      <span>NIF {clientInfo.nif || record.client_nif || '—'}</span>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">S/ fatura</span>
                                  )}
                                  
                                  {/* Mobile-only status badges */}
                                  <div className="flex items-center gap-1 mt-1 sm:hidden" data-testid={`mobile-status-${record.id}`}>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${record.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                      {record.payment_status === 'paid' ? '✓ Pago' : '✗ Pago'}
                                    </span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isInvoiced(record) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                      {isInvoiced(record) ? '✓ Faturado' : '✗ Faturado'}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                              
                              {/* Col 2: Técnico/Lead (hidden on mobile) */}
                              <TableCell className="hidden lg:table-cell">
                                <div className="flex items-center gap-1 text-sm">
                                  <User className="w-3 h-3 text-gray-400 shrink-0" />
                                  <span className="truncate max-w-[120px]" title={techInfo.leadName}>{techInfo.leadName}</span>
                                  {techInfo.otherCount > 0 && (
                                    <span className="text-xs text-gray-400" title={`+${techInfo.otherCount} técnico(s)`}>+{techInfo.otherCount}</span>
                                  )}
                                </div>
                              </TableCell>
                              
                              {/* Col 3: Data (hidden on mobile) */}
                              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                                {formatDate(record.closed_at)}
                              </TableCell>
                              
                              {/* Col 4: Valor */}
                              <TableCell className="text-right font-mono font-semibold">
                                €{(record.total_value || 0).toFixed(2)}
                              </TableCell>
                              
                              {/* Col 5: Pagamento */}
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {getPaymentMethodIcon(record.payment_method)}
                                  <span className="text-sm hidden sm:inline">{getPaymentMethodLabel(record.payment_method)}</span>
                                </div>
                                {/* Talão MB indicator */}
                                {(record.payment_method === 'card' || record.payment_method === 'MB') && record.mb_receipt_attached && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1.5 mt-0.5"
                                    onClick={async () => {
                                      try {
                                        const apId = record.fsm_service_appointment_id;
                                        if (!apId) { toast.error('AP não encontrada'); return; }
                                        const response = await axios.get(`${API}/fsm/appointments/${apId}/mb-receipt`);
                                        const data = response.data;
                                        if (data.image_base64) {
                                          const byteCharacters = atob(data.image_base64);
                                          const byteNumbers = new Array(byteCharacters.length);
                                          for (let i = 0; i < byteCharacters.length; i++) {
                                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                                          }
                                          const byteArray = new Uint8Array(byteNumbers);
                                          const blob = new Blob([byteArray], { type: data.content_type });
                                          window.open(URL.createObjectURL(blob), '_blank');
                                        }
                                      } catch (err) {
                                        toast.error('Erro ao obter talão MB');
                                      }
                                    }}
                                    title={record.mb_receipt_verified ? 'Talão verificado' : 'Talão por verificar'}
                                    data-testid={`mb-receipt-btn-${record.id}`}
                                  >
                                    <Camera className={`h-3.5 w-3.5 ${record.mb_receipt_verified ? 'text-green-600' : 'text-amber-600'}`} />
                                    {record.mb_receipt_verified ? (
                                      <CheckCircle2 className="h-3 w-3 text-green-600 ml-0.5" />
                                    ) : (
                                      <AlertTriangle className="h-3 w-3 text-amber-600 ml-0.5" />
                                    )}
                                  </Button>
                                )}
                              </TableCell>
                              
                              {/* Col 6: Estado (hidden on mobile) */}
                              <TableCell className="hidden sm:table-cell">
                                <div className="flex items-center gap-1" data-testid={`controls-${record.id}`}>
                                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5" title="Fechado pelo técnico">
                                    ✓ Téc
                                  </Badge>
                                  {record.payment_status === 'paid' ? (
                                    <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5" title="Pagamento confirmado">
                                      ✓ Pago
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-orange-600 border-orange-200 text-[10px] px-1.5" title="Pagamento pendente">
                                      ✗ Pago
                                    </Badge>
                                  )}
                                  {isInvoiced(record) ? (
                                    <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5" title={record.sage_invoice_ref ? `Fatura SAGE: ${record.sage_invoice_ref}` : 'Faturado'}>
                                      ✓ Fat
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-orange-600 border-orange-200 text-[10px] px-1.5" title="Sem fatura associada">
                                      ✗ Fat
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              
                              {/* Col 7: Ações */}
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1 items-center flex-wrap">
                                  {/* Ver AP / Detalhes */}
                                  {hasFsmAp ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/fsm-appointments?id=${record.fsm_service_appointment_id}`, { state: { returnTo: '/invoicing' } })}
                                      title="Ver AP completa"
                                      data-testid={`view-ap-${record.id}`}
                                    >
                                      <ArrowRight className="h-4 w-4 mr-1" />
                                      Ver AP
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDetailModal(record)}
                                      title="Ver detalhes do consumo"
                                      data-testid={`view-detail-${record.id}`}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  )}
                                  
                                  {/* Marcar Pago */}
                                  {record.payment_status !== 'paid' && !record.final_closed && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-green-700 border-green-200 hover:bg-green-50"
                                      onClick={() => markAsPaid(record)}
                                      title="Marcar como pago"
                                      data-testid={`mark-paid-${record.id}`}
                                    >
                                      <Euro className="h-4 w-4" />
                                    </Button>
                                  )}
                                  
                                  {/* Faturação */}
                                  {record.sage_invoice_ref ? (
                                    <Badge
                                      className="bg-emerald-100 text-emerald-700 border-emerald-200 font-mono text-[11px]"
                                      title={`Faturado em SAGE · Terceiro: ${record.sage_invoice_terceiro || '—'}`}
                                      data-testid={`sage-ref-${record.id}`}
                                    >
                                      {record.sage_invoice_ref}
                                    </Badge>
                                  ) : !record.invoiced_at && !record.final_closed ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openInvoiceModal(record)}
                                      title="Registar faturação"
                                      data-testid={`mark-invoiced-${record.id}`}
                                    >
                                      <Receipt className="h-4 w-4" />
                                    </Button>
                                  ) : record.invoiced_at ? (
                                    <Badge className="bg-blue-100 text-blue-700 text-[11px]" title={`Faturado: ${record.invoice_number || ''}`}>
                                      {record.invoice_number || 'Fat'}
                                    </Badge>
                                  ) : null}
                                  
                                  {/* Fecho Final */}
                                  {record.final_closed ? (
                                    <Badge className="bg-slate-800 text-white text-[10px]" title={`Fecho final: ${formatDate(record.final_closed_at)} por ${record.final_closed_by_name || '—'}`} data-testid={`final-closed-${record.id}`}>
                                      <Lock className="h-3 w-3 mr-0.5" />
                                      OK
                                    </Badge>
                                  ) : (canFinalize && canFinalClose(record)) && (
                                    <Button
                                      size="sm"
                                      className="bg-slate-800 hover:bg-slate-700 text-white"
                                      onClick={() => finalClose(record)}
                                      title="Fecho Final Admin"
                                      data-testid={`final-close-${record.id}`}
                                    >
                                      <BadgeCheck className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Consumo</DialogTitle>
            <DialogDescription>
              {selectedRecord?.ap_number} - {selectedRecord?.warehouse_name || selectedRecord?.warehouse_code}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Data Fecho</p>
                  <p className="font-medium">{formatDate(selectedRecord.closed_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Método Pagamento</p>
                  <p className="font-medium">{getPaymentMethodLabel(selectedRecord.payment_method)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estado</p>
                  <p className="font-medium">{selectedRecord.payment_status === 'paid' ? 'Pago' : 'Por Pagar'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Técnico</p>
                  <p className="font-medium">{selectedRecord.technician_name || selectedRecord.technician_id || 'N/A'}</p>
                </div>
                {selectedRecord.wants_invoice === true && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Fatura para</p>
                    <p className="font-medium">
                      {selectedRecord.client_name || '—'} · NIF {selectedRecord.client_nif || '—'} · {selectedRecord.client_email || '—'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artigo</TableHead>
                      <TableHead className="text-center">Qtd</TableHead>
                      <TableHead className="text-right">Preço</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRecord.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="font-mono text-xs text-slate-500">{item.sku}</div>
                          <div className="text-sm truncate max-w-[150px]">{item.name}</div>
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">€{(item.unit_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          €{((item.unit_price || 0) * item.quantity).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="border-t bg-slate-50/50 divide-y divide-slate-100">
                  {(selectedRecord.items_value !== undefined && selectedRecord.items_value !== null) && (
                    <div className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-slate-600">Subtotal Itens</span>
                      <span className="font-mono">€{(selectedRecord.items_value || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {(selectedRecord.labor_value !== undefined && selectedRecord.labor_value !== null) && (
                    <div className="flex items-center justify-between px-3 py-2 text-sm" data-testid="invoicing-labor-row">
                      <span className="text-slate-600">Mão de Obra</span>
                      <span className="font-mono">€{Number(selectedRecord.labor_value).toFixed(2)}</span>
                    </div>
                  )}
                  {(selectedRecord.travel_value !== undefined && selectedRecord.travel_value !== null) && (
                    <div className="flex items-center justify-between px-3 py-2 text-sm" data-testid="invoicing-travel-row">
                      <span className="text-slate-600">Deslocação</span>
                      <span className="font-mono">€{Number(selectedRecord.travel_value).toFixed(2)}</span>
                    </div>
                  )}
                  {(selectedRecord.other_value || 0) > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 text-sm" data-testid="invoicing-other-row">
                      <span className="text-slate-600 truncate max-w-[60%]" title={selectedRecord.other_description || 'Outros'}>
                        Outros{selectedRecord.other_description ? `: ${selectedRecord.other_description}` : ''}
                      </span>
                      <span className="font-mono">€{Number(selectedRecord.other_value).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-3 py-3 bg-slate-50">
                    <span className="font-medium">Total</span>
                    <span className="text-lg font-bold font-mono">€{(selectedRecord.total_value || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedRecord.sage_invoice_ref && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg" data-testid="sage-invoice-details">
                  <p className="text-sm text-emerald-900 font-medium mb-1">
                    🔗 Documento SAGE: <span className="font-mono">{selectedRecord.sage_invoice_ref}</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-emerald-800">
                    {selectedRecord.sage_invoice_terceiro && (
                      <div><span className="text-emerald-600">Terceiro:</span> {selectedRecord.sage_invoice_terceiro}</div>
                    )}
                    {selectedRecord.sage_invoice_data && (
                      <div><span className="text-emerald-600">Data SAGE:</span> {selectedRecord.sage_invoice_data}</div>
                    )}
                    {selectedRecord.sage_invoice_total !== undefined && (
                      <div><span className="text-emerald-600">Total SAGE:</span> €{Number(selectedRecord.sage_invoice_total || 0).toFixed(2)}</div>
                    )}
                    {selectedRecord.sage_invoice_linked_at && (
                      <div><span className="text-emerald-600">Associado:</span> {formatDate(selectedRecord.sage_invoice_linked_at)}</div>
                    )}
                  </div>
                </div>
              )}

              {selectedRecord.invoiced_at && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Faturado:</strong> {formatDate(selectedRecord.invoiced_at)}
                    {selectedRecord.invoice_number && ` - Fatura: ${selectedRecord.invoice_number}`}
                  </p>
                </div>
              )}

              {selectedRecord.final_closed && (
                <div className="bg-slate-800 text-white p-3 rounded-lg" data-testid="final-closed-details">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Fecho Final (Admin)
                  </p>
                  <p className="text-xs text-slate-300 mt-1">
                    Concluído em {formatDate(selectedRecord.final_closed_at)} por {selectedRecord.final_closed_by_name || '—'}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDetailModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Invoiced Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como Faturado</DialogTitle>
            <DialogDescription>
              {selectedRecord?.ap_number} - €{(selectedRecord?.total_value || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Número da Fatura (opcional)</label>
              <Input
                placeholder="Ex: FT 2026/001234"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="mt-1"
                data-testid="invoice-number-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>
              Cancelar
            </Button>
            <Button onClick={markAsInvoiced} disabled={processing} className="bg-[#C41E3A] hover:bg-[#a01830]">
              {processing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="h-4 w-4 mr-2" />
              )}
              Confirmar Faturação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
